import { Toaster } from '@/components/ui/toaster'
import { Toaster as Sonner } from '@/components/ui/sonner'
import { TooltipProvider } from '@/components/ui/tooltip'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import {
	BrowserRouter,
	Routes,
	Route,
	Navigate,
	Outlet,
	useLocation,
	useParams,
	matchPath,
} from 'react-router-dom'
import { SidebarProvider, SidebarTrigger } from '@/components/ui/sidebar'
import { AppSidebar } from '@/components/AppSidebar'
import { AgentPresenceTracker } from '@/components/agent-status/AgentStatusIndicator'
import { useEffect, useState } from 'react'
import { Loader2 } from 'lucide-react'
import { Helmet, HelmetProvider } from 'react-helmet-async'
import { ErrorBoundary } from './components/ErrorBoundary'
import Landing from './pages/Landing'
import Index from './pages/Index'
import Widgets from './pages/Widgets'
import CreateWidget from './pages/CreateWidget'
import EditWidget from './pages/EditWidget'
import WidgetDetails from './pages/WidgetDetails'
import Settings from './pages/Settings'
import NotificationSettingsPage from './pages/NotificationSettings'
import LiveChat from './pages/LiveChat'
import Members from './pages/Members'
import Integrations from './pages/Integrations'
import NotFound from './pages/NotFound'
import Auth from './pages/Auth'
import AuthCallback from './pages/AuthCallback'
import AuthDebug from './pages/AuthDebug'
import AuthForceLogin from './pages/AuthForceLogin'
import EmbedWidget from './pages/EmbedWidget'
import Responses from './pages/Responses'
import WidgetSuccess from './pages/WidgetSuccess'
import BugReports from './pages/BugReports'
import Mailbox from './pages/Mailbox'
import Reviews from './pages/Reviews'
import FeatureRequests from './pages/FeatureRequests'
import OnboardingPage from './pages/OnboardingPage'
import { ProgramProvider } from './context/ProgramContext'
import { AuthProvider, useAuth } from './context/AuthContext'
import { TeamProvider } from './context/TeamContext'
import { OnboardingGuard } from './guards/OnboardingGuard'
import { WidgetsGuard } from './guards/WidgetsGuard'
import { AdminManagerGuard } from './guards/AdminManagerGuard'
import TermsAndConditions from './pages/Terms'
import PrivacyPolicy from './pages/PrivacyPolicies'
import LegalNotice from './pages/LegalNotice'
import CookiePolicy from './pages/CookiePolicy'
import { CookieConsent } from './components/legal/CookieConsent'
import DocsInstallation from './pages/DocsInstallation'
import AlternativePSEOPage from './pages/AlternativePSEOPage'
import AlternativesPage from './pages/AlternativesPage'
import ComparePage from './pages/ComparePage'
import CompareArticlePage from './pages/CompareArticlePage'
import CategoryPage from './pages/CategoryPage'
import PublicReviewPage from './pages/PublicReviewPage'
import ProgramSettings from './pages/ProgramSettings'
import PublicRoadmapPage from './pages/public/PublicRoadmapPage'
import { PublicPortalLayout } from './layouts/PublicPortalLayout'
import RoadmapSettingsPage from './pages/RoadmapSettingsPage'
import RoadmapPage from './pages/RoadmapPage'
import Blog from './pages/Blog'
import BlogArticle from './pages/BlogArticle'
import Team from './pages/Team'
import AffiliateProgramPage from './pages/AffiliateProgramPage'
import Inbox from './pages/Inbox'
import KnowledgeBase from './pages/KnowledgeBase'
import BrandMentions from './pages/BrandMentions'
import { TeamSlugProvider, useTeamSlug } from './context/TeamSlugContext'
import PublicProfilePage from './pages/public/PublicProfilePage'
import PublicAuthCallback from './pages/public/PublicAuthCallback'
import ProgramInviteAccept from './pages/ProgramInviteAccept'
import { FeedbaskIdentificationManager } from './components/FeedbaskIdentificationManager'
import { AdminImpersonationBanner } from './components/admin/AdminImpersonationBanner'
import { AdminEmailGuard } from './guards/AdminEmailGuard'
import AdminLayout from './pages/admin/AdminLayout'
import AdminAnalyticsPage from './pages/admin/AdminAnalyticsPage'
import AdminUsersPage from './pages/admin/AdminUsersPage'
import { useLanguageFromUrl } from './hooks/useLanguageFromUrl'
import { MARKETING_ROUTES } from './lib/marketingRoutes'
import { canonicalizePath, toAbsoluteUrl } from './lib/seo'

import {
	WebsiteFeedbackWidget,
	InAppFeedbackWidget,
	FeatureVotingTool,
	PublicRoadmapSoftware,
	TestimonialsWidget,
	BugReportWidget,
} from './pages/product'
import { LimitWarningTopBanner } from './components/limits'
import { ScrollToTop } from './components/ScrollToTop'

// Create a client with more aggressive refetching
const queryClient = new QueryClient({
	defaultOptions: {
		queries: {
			// Enable automatic refetching when window regains focus
			refetchOnWindowFocus: true,
			// Enable refetching when the component mounts
			refetchOnMount: true,
			// Enable refetching when the network reconnects
			refetchOnReconnect: true,
			// Set a reasonable staleTime to avoid excessive refetching
			staleTime: 1000 * 60, // 1 minute
			// Set a reasonable gcTime (previously cacheTime)
			gcTime: 1000 * 60 * 5, // 5 minutes
			// Retry failed queries
			retry: 1,
		},
	},
})

const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
	const { session, loading, isInitialized } = useAuth()
	const [isInitializing, setIsInitializing] = useState(true)

	// Add a small delay to handle hot reload race conditions
	useEffect(() => {
		const timer = setTimeout(() => {
			setIsInitializing(false)
		}, 100)

		return () => clearTimeout(timer)
	}, [])

	// Wait for auth to be initialized before making routing decisions
	if (!isInitialized || loading || isInitializing) {
		return (
			<div className="flex h-screen w-full items-center justify-center">
				<Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
			</div>
		)
	}

	if (!session) {
		return <Navigate to="/auth" replace />
	}

	return <>{children}</>
}

// Layout component with sidebar for protected routes
const DashboardLayout = () => {
	return (
		<div className="flex h-screen w-full flex-col overflow-hidden">
			<AdminImpersonationBanner />
			<div className="flex min-h-0 flex-1 overflow-hidden">
				<SidebarProvider>
					<AppSidebar />
					<main className="flex flex-1 flex-col overflow-hidden bg-neutral-50 dark:bg-muted/20">
						<AgentPresenceTracker />
						<div className="flex items-center border-b p-2 flex-shrink-0 md:hidden">
							<SidebarTrigger />
						</div>
						<div className="flex-1 overflow-auto bg-neutral-50 dark:bg-muted/20">
							<Outlet />
						</div>
					</main>
				</SidebarProvider>
			</div>
		</div>
	)
}

// Protected layout that combines authentication and dashboard layout
const ProtectedDashboardLayout = () => {
	return (
		<ProtectedRoute>
			<DashboardLayout />
		</ProtectedRoute>
	)
}

// Layout for routes that require onboarding to be complete
const GuardedDashboardLayout = () => {
	return (
		<TeamProvider>
			<ProgramProvider>
				<FeedbaskIdentificationManager />
				<OnboardingGuard>
					<DashboardLayout />
				</OnboardingGuard>
			</ProgramProvider>
		</TeamProvider>
	)
}

const BillingRouteRedirect = () => {
	const location = useLocation()
	const params = new URLSearchParams(location.search)
	params.set('tab', 'billing')
	return <Navigate to={`/settings?${params.toString()}`} replace />
}

const LegacyAlternativeRedirect = () => {
	const { slug } = useParams<{ slug?: string }>()
	const to = slug ? `/alternatives/${slug}` : '/alternatives'
	return <Navigate to={to} replace />
}

type RouteSeoMeta = {
	title: string
	description: string
	noindex?: boolean
}

const DEFAULT_ROUTE_SEO: RouteSeoMeta = {
	title: 'Feedbask - Customer support software with AI',
	description:
		'Collect customer feedback, NPS surveys, bug reports, and feature requests with one embeddable widget.',
}

const routeSeoPatterns: Array<{ pattern: string; meta: RouteSeoMeta }> = [
	{
		pattern: '/dashboard',
		meta: {
			title: 'Dashboard | Feedbask',
			description: 'Track customer feedback, insights, and activity in your Feedbask dashboard.',
		},
	},
	{
		pattern: '/widgets',
		meta: {
			title: 'Widgets | Feedbask',
			description: 'Create and manage feedback widgets to collect user insights across your product.',
		},
	},
	{
		pattern: '/widgets/new',
		meta: {
			title: 'Create Widget | Feedbask',
			description: 'Create a new feedback widget and start collecting customer insights in minutes.',
		},
	},
	{
		pattern: '/widgets/edit/:id',
		meta: {
			title: 'Edit Widget | Feedbask',
			description: 'Update your feedback widget settings, design, and behavior.',
		},
	},
	{
		pattern: '/widgets/success/:widgetId',
		meta: {
			title: 'Widget Created | Feedbask',
			description: 'Your widget is ready. Install it and start collecting customer feedback.',
		},
	},
	{
		pattern: '/widgets/:id',
		meta: {
			title: 'Widget Details | Feedbask',
			description: 'View and manage widget details, responses, and configuration.',
		},
	},
	{
		pattern: '/responses',
		meta: {
			title: 'Responses | Feedbask',
			description: 'Review and analyze incoming customer feedback responses in one place.',
		},
	},
	{
		pattern: '/mailbox',
		meta: {
			title: 'Mailbox | Feedbask',
			description: 'Manage incoming conversations and customer feedback from your mailbox.',
		},
	},
	{
		pattern: '/inbox',
		meta: {
			title: 'Inbox | Feedbask',
			description: 'Follow and triage incoming customer messages and feedback.',
		},
	},
	{
		pattern: '/features',
		meta: {
			title: 'Feature Requests | Feedbask',
			description: 'Review and prioritize feature requests from your users.',
		},
	},
	{
		pattern: '/bugs',
		meta: {
			title: 'Bug Reports | Feedbask',
			description: 'Track, triage, and resolve bug reports submitted by customers.',
		},
	},
	{
		pattern: '/reviews',
		meta: {
			title: 'Reviews | Feedbask',
			description: 'Collect, manage, and publish customer reviews from one dashboard.',
		},
	},
	{
		pattern: '/roadmap',
		meta: {
			title: 'Roadmap | Feedbask',
			description: 'Share your product roadmap and keep users aligned on upcoming work.',
		},
	},
	{
		pattern: '/settings',
		meta: {
			title: 'Settings | Feedbask',
			description: 'Manage your workspace, billing, and account settings in Feedbask.',
		},
	},
	{
		pattern: '/program-settings',
		meta: {
			title: 'Program Settings | Feedbask',
			description: 'Configure program-level settings for your feedback workflows.',
		},
	},
	{
		pattern: '/roadmap-settings',
		meta: {
			title: 'Roadmap Settings | Feedbask',
			description: 'Configure your public roadmap preferences and visibility options.',
		},
	},
	{
		pattern: '/members',
		meta: {
			title: 'Members | Feedbask',
			description: 'Invite and manage team members collaborating on customer feedback.',
		},
	},
	{
		pattern: '/integrations',
		meta: {
			title: 'Integrations | Feedbask',
			description: 'Connect Feedbask with your tools to streamline feedback workflows.',
		},
	},
	{
		pattern: '/notification-settings',
		meta: {
			title: 'Notification Settings | Feedbask',
			description: 'Customize how and when your team receives feedback notifications.',
		},
	},
	{
		pattern: '/knowledge-base',
		meta: {
			title: 'Knowledge Base | Feedbask',
			description: 'Build and manage your public knowledge base and support content.',
		},
	},
	{
		pattern: '/brand-mentions',
		meta: {
			title: 'Brand Mentions | Feedbask',
			description: 'Track public mentions of your product on X, Reddit, LinkedIn, and Facebook.',
		},
	},
	{
		pattern: '/live-chat',
		meta: {
			title: 'Live Chat | Feedbask',
			description: 'Manage real-time customer conversations and support interactions.',
		},
	},
	{
		pattern: '/onboarding',
		meta: {
			title: 'Onboarding | Feedbask',
			description: 'Set up your workspace and start collecting customer feedback quickly.',
		},
	},
	{
		pattern: '/auth',
		meta: {
			title: 'Sign In | Feedbask',
			description: 'Sign in to your Feedbask account to manage feedback and product insights.',
			noindex: true,
		},
	},
	{
		pattern: '/auth/callback',
		meta: {
			title: 'Authentication | Feedbask',
			description: 'Authentication callback page.',
			noindex: true,
		},
	},
	{
		pattern: '/auth/debug',
		meta: {
			title: 'Auth Debug | Feedbask',
			description: 'Authentication diagnostics page.',
			noindex: true,
		},
	},
	{
		pattern: '/auth/force-login',
		meta: {
			title: 'Force Login | Feedbask',
			description: 'Secure authentication page for Feedbask.',
			noindex: true,
		},
	},
	{
		pattern: '/invite/program/:token',
		meta: {
			title: 'Program Invitation | Feedbask',
			description: 'Accept your invitation to join a Feedbask program.',
			noindex: true,
		},
	},
	{
		pattern: '/embed/:id',
		meta: {
			title: 'Embedded Widget | Feedbask',
			description: 'Embedded feedback widget page.',
			noindex: true,
		},
	},
		{
			pattern: '/privacy',
			meta: {
				title: 'Privacy Policy | Feedbask',
				description: 'Read Feedbask privacy policy and data handling commitments.',
			},
		},
		{
			pattern: '/tos',
			meta: {
				title: 'Terms of Service | Feedbask',
				description: 'Read Feedbask terms of service and platform usage conditions.',
			},
		},
		{
			pattern: '/legal',
			meta: {
				title: 'Legal notice | Feedbask',
				description: 'Publisher and hosting information for Feedbask.',
			},
		},
		{
			pattern: '/mentions-legales',
			meta: {
				title: 'Mentions légales | Feedbask',
				description: 'Mentions légales de Feedbask.',
			},
		},
		{
			pattern: '/cookies',
			meta: {
				title: 'Cookie policy | Feedbask',
				description: 'How Feedbask uses cookies and similar technologies.',
			},
		},
	{
		pattern: '/docs',
		meta: {
			title: 'Documentation | Feedbask',
			description: 'Set up and install Feedbask with the official documentation.',
		},
	},
	{
		pattern: '/blog',
		meta: {
			title: 'Blog | Feedbask',
			description: 'Explore product feedback best practices, guides, and updates from Feedbask.',
		},
	},
	{
		pattern: '/blog/:slug',
		meta: {
			title: 'Article | Feedbask Blog',
			description: 'Read in-depth guides and updates from the Feedbask blog.',
		},
	},
	{
		pattern: '/team',
		meta: {
			title: 'Our Team | Feedbask',
			description: 'Meet Tarun and Pauline, the co-founders of Feedbask.',
		},
	},
	{
		pattern: '/affiliate-program',
		meta: {
			title: 'Affiliate Program | Feedbask',
			description:
				'Earn 30% recurring commission promoting Feedbask. Join our affiliate program and turn your audience into long-term passive income.',
		},
	},
]

const GlobalRouteSeo = () => {
	const location = useLocation()
	const normalizedPath = canonicalizePath(location.pathname)
	const matchedPattern = routeSeoPatterns.find(({ pattern }) =>
		matchPath({ path: pattern, end: true }, normalizedPath)
	)
	const meta = matchedPattern?.meta ?? DEFAULT_ROUTE_SEO
	const canonicalUrl = toAbsoluteUrl(normalizedPath)

	return (
		<Helmet>
			<title>{meta.title}</title>
			<meta name="description" content={meta.description} />
			<link rel="canonical" href={canonicalUrl} />
			<meta property="og:title" content={meta.title} />
			<meta property="og:description" content={meta.description} />
			<meta property="og:url" content={canonicalUrl} />
			<meta name="twitter:title" content={meta.title} />
			<meta name="twitter:description" content={meta.description} />
			{meta.noindex ? (
				<meta name="robots" content="noindex,follow" />
			) : (
				<meta name="robots" content="index,follow,max-snippet:-1,max-image-preview:large,max-video-preview:-1" />
			)}
		</Helmet>
	)
}

// Component to determine routing based on subdomain
const AppRoutes = () => {
	const { isPortal } = useTeamSlug()
	useLanguageFromUrl()

	// If it's a portal (subdomain), render only portal routes
	if (isPortal) {
		return (
			<>
				{/* Tenant portal subdomains share the same SPA shell as the main
				    marketing site. Without noindex Google treats every tenant
				    roadmap as a near-duplicate of www.feedbask.com, which blocks
				    indexing site-wide. */}
				<Helmet>
					<meta name="robots" content="noindex,follow" />
				</Helmet>
				<Routes>
					<Route path="/" element={<PublicPortalLayout />}>
						<Route index element={<Navigate to="/roadmap" replace />} />
						<Route path="roadmap" element={<PublicRoadmapPage />} />
						<Route path="auth/callback" element={<PublicAuthCallback />} />
						<Route
							path="profile"
							element={
								<ProtectedRoute>
									<TeamProvider>
										<FeedbaskIdentificationManager />
										<PublicProfilePage />
									</TeamProvider>
								</ProtectedRoute>
							}
						/>
					</Route>
					<Route path="*" element={<NotFound />} />
				</Routes>
			</>
		)
	}

	// Otherwise, render the main marketing site and dashboard routes
	return (
		<>
			<GlobalRouteSeo />
			<Routes>
				{/* Marketing site routes */}
				<Route path="/" element={<Landing />} />
				<Route path={MARKETING_ROUTES.home.fr} element={<Landing />} />

				<Route path="/alternative-:slug" element={<LegacyAlternativeRedirect />} />
				<Route path="/alternatives" element={<AlternativesPage />} />
				<Route path={MARKETING_ROUTES.alternatives.fr} element={<AlternativesPage />} />
				<Route path="/alternatives/:toolSlug" element={<AlternativePSEOPage />} />
				<Route path="/compare" element={<ComparePage />} />
				<Route path="/affiliate-program" element={<AffiliateProgramPage />} />
				<Route path={MARKETING_ROUTES.compare.fr} element={<ComparePage />} />
				<Route path="/compare/:slug" element={<CompareArticlePage />} />
				<Route path="/categories/features" element={<CategoryPage />} />
				<Route path={MARKETING_ROUTES.categoriesFeatures.fr} element={<CategoryPage />} />
				<Route
					path="/categories/features/:categorySlug"
					element={<CategoryPage />}
				/>
				<Route path="/technologies" element={<CategoryPage />} />
				<Route path="/technologies/:categorySlug" element={<CategoryPage />} />
				<Route path="/auth" element={<Auth />} />
				{/* Main domain auth callback - handles OAuth and magic links */}
				<Route path="/auth/callback" element={<AuthCallback />} />
				<Route path="/auth/debug" element={<AuthDebug />} />
				<Route path="/auth/force-login" element={<AuthForceLogin />} />
				<Route path="/invite/program/:token" element={<ProgramInviteAccept />} />
				<Route path="/embed/:id" element={<EmbedWidget />} />
				<Route path="/tos" element={<TermsAndConditions />} />
				<Route path="/cgu" element={<TermsAndConditions />} />
				<Route path="/cgv" element={<TermsAndConditions />} />
				<Route path="/privacy" element={<PrivacyPolicy />} />
				<Route path="/legal" element={<LegalNotice />} />
				<Route path="/mentions-legales" element={<LegalNotice />} />
				<Route path="/cookies" element={<CookiePolicy />} />
				<Route path="/politique-cookies" element={<CookiePolicy />} />
				<Route path="/docs" element={<DocsInstallation />} />
				<Route path="/team" element={<Team />} />
				{/* Blog routes */}
				<Route path="/blog" element={<Blog />} />
				<Route path="/blog/:slug" element={<BlogArticle />} />
				{/* New product pages */}
				<Route
					path="/product/website-feedback-widget"
					element={<WebsiteFeedbackWidget />}
				/>
				<Route
					path={MARKETING_ROUTES.websiteFeedbackWidget.fr}
					element={<WebsiteFeedbackWidget />}
				/>
				<Route
					path="/product/in-app-feedback-widget"
					element={<InAppFeedbackWidget />}
				/>
				<Route
					path={MARKETING_ROUTES.inAppFeedbackWidget.fr}
					element={<InAppFeedbackWidget />}
				/>
				<Route
					path="/product/feature-voting-tool"
					element={<FeatureVotingTool />}
				/>
				<Route
					path={MARKETING_ROUTES.featureVotingTool.fr}
					element={<FeatureVotingTool />}
				/>
				<Route
					path="/product/public-roadmap-software"
					element={<PublicRoadmapSoftware />}
				/>
				<Route
					path={MARKETING_ROUTES.publicRoadmapSoftware.fr}
					element={<PublicRoadmapSoftware />}
				/>
				<Route
					path="/product/testimonials-widget"
					element={<TestimonialsWidget />}
				/>
				<Route
					path={MARKETING_ROUTES.testimonialsWidget.fr}
					element={<TestimonialsWidget />}
				/>
				<Route path="/product/bug-report-widget" element={<BugReportWidget />} />
				<Route
					path={MARKETING_ROUTES.bugReportWidget.fr}
					element={<BugReportWidget />}
				/>
				<Route
					path="/settings/billing"
					element={<BillingRouteRedirect />}
				/>
				<Route
					path="/reviews/:program_identifier"
					element={<PublicReviewPage />}
				/>

				{/* Onboarding route */}
				<Route
					path="/onboarding"
					element={
						<ProtectedRoute>
							<TeamProvider>
								<ProgramProvider>
									<FeedbaskIdentificationManager />
									<OnboardingPage />
								</ProgramProvider>
							</TeamProvider>
						</ProtectedRoute>
					}
				/>

				{/* Dashboard routes */}
				<Route
					path="/"
					element={
						<ProtectedRoute>
							<GuardedDashboardLayout />
						</ProtectedRoute>
					}>
					<Route path="dashboard" element={<Index />} />
					<Route path="widgets" element={<WidgetsGuard><Widgets /></WidgetsGuard>} />
					<Route path="widgets/new" element={<WidgetsGuard><CreateWidget /></WidgetsGuard>} />
					<Route path="widgets/success/:widgetId" element={<WidgetsGuard><WidgetSuccess /></WidgetsGuard>} />
					<Route path="widgets/edit/:id" element={<WidgetsGuard><EditWidget /></WidgetsGuard>} />
					<Route path="widgets/:id" element={<WidgetsGuard><WidgetDetails /></WidgetsGuard>} />
					<Route path="responses" element={<Responses />} />
					<Route path="bugs" element={<BugReports />} />
					<Route path="mailbox" element={<Mailbox />} />
					<Route path="features" element={<FeatureRequests />} />
					<Route path="reviews" element={<Reviews />} />
					<Route path="roadmap" element={<RoadmapPage />} />
					<Route path="settings" element={<Settings />} />
					<Route path="notification-settings" element={<NotificationSettingsPage />} />
					<Route path="live-chat" element={<LiveChat />} />
					<Route path="members" element={<Members />} />
					<Route path="integrations" element={<AdminManagerGuard><Integrations /></AdminManagerGuard>} />
					<Route path="program-settings" element={<AdminManagerGuard><ProgramSettings /></AdminManagerGuard>} />
					<Route path="roadmap-settings" element={<AdminManagerGuard><RoadmapSettingsPage /></AdminManagerGuard>} />
					<Route path="inbox" element={<Inbox />} />
					<Route path="knowledge-base" element={<KnowledgeBase />} />
					<Route path="brand-mentions" element={<BrandMentions />} />
				</Route>
				<Route
					path="/admin"
					element={
						<ProtectedRoute>
							<AdminEmailGuard>
								<AdminLayout />
							</AdminEmailGuard>
						</ProtectedRoute>
					}
				>
					<Route index element={<Navigate to="/admin/users" replace />} />
					<Route path="users" element={<AdminUsersPage />} />
					<Route path="analytics" element={<AdminAnalyticsPage />} />
				</Route>

				<Route path="*" element={<NotFound />} />
			</Routes>
		</>
	)
}

const App = () => (
	<QueryClientProvider client={queryClient}>
		<ErrorBoundary queryClient={queryClient}>
			<TooltipProvider delayDuration={0}>
				<AuthProvider>
					<HelmetProvider>
						<Toaster />
						<Sonner position="top-center" />
						<BrowserRouter>
							<ScrollToTop />
							<TeamSlugProvider>
								<SidebarProvider>
									<div className="min-h-screen w-full">
										<AppRoutes />
										<CookieConsent />
									</div>
								</SidebarProvider>
							</TeamSlugProvider>
						</BrowserRouter>
					</HelmetProvider>
				</AuthProvider>
			</TooltipProvider>
		</ErrorBoundary>
	</QueryClientProvider>
)

export default App
